import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProductionTranslationService } from '../core/services/production-translation.service';
import { UnifiedShipment } from '../core/models/unified-shipment.models';
import { normalizeBefretDeliveryMethod } from '../core/models/delivery-axes.models';
import { ShipmentRepositoryService } from '../services/shipment-repository.service';
import { ShipmentFormatterService } from '../features/ship/services/shipment-formatter.service';
import { ShipmentPermissionsService } from '../services/shipment-permissions.service';
import { NotificationService } from '../core/services/notification/notification.service';
import { LabelDownloadService } from '../core/services/label-download.service';
import { parsePhoneString } from '../core/utils/phone-prefix.util';
import { DeclaredValue, readDeclaredValue } from '../core/utils/declared-value';
import { Functions, httpsCallable } from '@angular/fire/functions';

/**
 * MANAGER ORDER DETAILS - Vue exhaustive pour support client
 *
 * Affiche TOUTES les informations disponibles sur une commande:
 * - Données client (sender, receiver)
 * - Données colis (poids, dimensions, options)
 * - Pricing détaillé (base price, surcharges, taxes)
 * - DPD Integration (tracking, shipmentId, label URL)
 * - Payment Flow (Stripe session, payment intent, webhook status)
 * - Metadata (version, source, flags, migration info)
 * - Status History (timeline complète des événements)
 * - Phase Timeline (progression logistique)
 * - Webhook Processing Status (stages détaillés)
 *
 * Fonctionnalités:
 * - ✅ Mode lecture seule pour visualisation rapide
 * - ✅ Mode édition (admin only) avec formulaire complet
 * - ✅ CRUD operations (Update, Delete)
 * - ✅ Téléchargement étiquettes DPD
 * - ✅ Export données JSON
 * - ✅ Copie tracking numbers dans clipboard
 */
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SharedModule } from '../shared/shared.module';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { hasDownloadableLabel } from '../core/utils/label-availability.util';

/**
 * Un bouton d'action rapide de l'écran manager.
 *
 * #1586 — ce type portait un champ `phase`, et les SEPT actions d'incident y
 * déclaraient `phase: 'delivered'`. Cette valeur n'a jamais été écrite nulle
 * part : `quickAction()` n'envoie que le statut à la Cloud Function, qui pose
 * la phase elle-même, dans la même transaction — et pour un incident elle pose
 * `incident`, jamais `delivered` (`functions/src/domains/shipment/shipment-phase.ts`,
 * `timeline-sync.service.ts`). Aucun colis perdu n'a donc jamais été annoncé
 * « livré ».
 *
 * Mais une constante fausse que le code n'utilise pas coûte le temps de qui la
 * lit : le 29/08/2026 elle a fait conclure à un relecteur que le trigger de
 * phase annonçait une livraison sur un colis saisi en douane, et il a fallu
 * descendre jusqu'au mapping serveur pour établir que non. C'était un piège
 * armé, pas une coquille.
 *
 * Le champ est donc renommé pour ce qu'il fait RÉELLEMENT — désigner le lieu
 * où le colis se trouve après l'action, pour horodater l'événement au bon
 * endroit — et il ne subsiste que là où c'est vrai : les deux actions
 * « Arrivé entrepôt Tubize ». Une action qui ne le porte pas n'attache aucun
 * lieu à l'événement, ce qui est le comportement d'avant, à l'identique.
 */
export interface ManagerQuickAction {
  label: string;
  status: string;
  /** Seul lieu que l'écran sache nommer. Absent = la CF n'horodate aucun lieu. */
  locationHint?: 'warehouse';
  icon: string;
  color: string;
}

@Component({
    selector: 'app-manager-order-details',
    templateUrl: './manager-order-details.component.html',
    styleUrls: ['./manager-order-details.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule, SharedModule, MatIconModule, MatProgressSpinnerModule, MatProgressBarModule, MatCardModule, MatCheckboxModule, MatChipsModule, MatDividerModule, MatTooltipModule, MatButtonModule]
})
export class ManagerOrderDetailsComponent implements OnInit, OnDestroy {
  trackingID: string = '';
  shipment: UnifiedShipment | null = null;
  isLoading: boolean = true;
  error: string = '';

  // Make JSON available in template
  JSON = JSON;

  // Mode édition
  isEditMode: boolean = false;
  isAdmin: boolean = false;
  isSaving: boolean = false;

  // 🆕 Mode collecte manuelle (bypass DPD)
  isManualCollection: boolean = false;

  // Formulaire d'édition
  editForm!: FormGroup;

  // 🆕 Unsubscribe subject
  private destroy$ = new Subject<void>();

  // Notification logs
  notificationLogs: any[] = [];
  isRetrying: { [id: string]: boolean } = {};

  // Sections collapsibles
  expandedSections: { [key: string]: boolean } = {
    basicInfo: true,
    customerInfo: true,
    parcelInfo: true,
    pricing: true,
    dpdIntegration: true,
    paymentFlow: true,
    metadata: false,
    statusHistory: false,
    phaseTimeline: false,
    webhookStatus: false,
    notificationLogs: false,
    rawData: false
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private translate: ProductionTranslationService,
    // 🆕 New services
    private repository: ShipmentRepositoryService,
    public formatter: ShipmentFormatterService,
    private permissions: ShipmentPermissionsService,
    private notification: NotificationService,
    private labelDownloadService: LabelDownloadService,
    private functions: Functions
  ) {
    this.initForm();
  }

  /**
   * ✅ NOUVEAU: Obtenir le TimelineStep actuel basé sur status.current
   * Utilise StatusMappingService pour convertir le statut technique en TimelineStep
   */
  getCurrentTimelineStep(): string {
    if (!this.shipment) return '';

    return this.formatter.getTimelineStep(
      this.shipment.status?.current,
      this.shipment.category,
      this.shipment.webhookStatus
    );
  }

  ngOnInit(): void {
    // Vérifier si l'utilisateur est admin
    this.checkAdminStatus();

    // Récupérer le trackingID depuis l'URL
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.trackingID = params['trackingID'];

        // Check if edit mode is requested via query param
        if (params['edit'] === 'true' && this.isAdmin) {
          this.isEditMode = true;
        }

        if (this.trackingID) {
          this.loadShipmentDetails();
        } else {
          this.error = 'No tracking ID provided';
          this.isLoading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialiser le formulaire d'édition COMPLET
   */
  private initForm(): void {
    this.editForm = this.fb.group({
      // Sender
      senderName: ['', Validators.required],
      senderEmail: ['', [Validators.required, Validators.email]],
      senderPhone: ['', Validators.required],
      senderStreet: [''],
      // #1640 — ce contrôle MANQUAIT alors que le template le lie
      // (`[formControl]="$any(editForm.get('senderHouseNumber'))"`). La
      // liaison valait donc `$any(null)`, et `setUpControl(null, dir)` lève
      // dès la première détection de changements en mode édition : en dev
      // « Cannot find control with », en build de production un TypeError
      // dans `getControlValidators`. L'écran d'édition cassait à l'ouverture,
      // et le `$any(...)` est exactement ce qui empêchait le compilateur de
      // le dire. Le numéro de rue est une vraie donnée du modèle
      // (`UnifiedAddress.houseNumber`) : DPD le reçoit en `houseNo` pour la
      // collecte (`reschedule-collection.ts`).
      senderHouseNumber: [''],
      senderPostalCode: [''],
      senderCity: [''],
      senderCountry: [''],

      // Receiver
      receiverName: ['', Validators.required],
      receiverEmail: ['', [Validators.required, Validators.email]],
      receiverPhone: ['', Validators.required],
      receiverStreet: [''],
      receiverPostalCode: [''],
      receiverCity: [''],
      receiverCountry: [''],

      // Parcel
      weight: ['', [Validators.required, Validators.min(0)]],
      length: ['', [Validators.required, Validators.min(0)]],
      width: ['', [Validators.required, Validators.min(0)]],
      height: ['', [Validators.required, Validators.min(0)]],
      description: [''],

      // #1573 — plus de contrôles `basePrice`/`weightSurcharge`/`serviceFee`/
      // `taxes`/`total` ni `currentStatus` : aucun widget du template ne les
      // câble (le template déclare 20 `[formControl]`, aucun pour ces six).
      // Les garder ici, c'est garder l'aller-retour qui écrivait `0` sur un
      // prix absent — même geste que #1401 pour `currentPhase`.
    });
  }

  /**
   * 🆕 Vérifier si l'utilisateur est admin (refactoré avec service)
   */
  private checkAdminStatus(): void {
    this.permissions.isAdmin()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAdmin => {
        this.isAdmin = isAdmin;
      });
  }

  /**
   * 🆕 Peupler le formulaire (utilise formatter service)
   */
  private populateForm(): void {
    if (!this.shipment) return;

    this.editForm.patchValue({
      // Sender
      senderName: this.shipment.customerInfo?.sender?.name || '',
      senderEmail: this.shipment.customerInfo?.sender?.email || '',
      senderPhone: this.formatter.formatPhone(this.shipment.customerInfo?.sender?.phone) || '',
      senderStreet: this.shipment.customerInfo?.sender?.address?.street || '',
      senderHouseNumber: this.shipment.customerInfo?.sender?.address?.houseNumber || '',
      senderPostalCode: this.shipment.customerInfo?.sender?.address?.zipCode || '',
      senderCity: this.shipment.customerInfo?.sender?.address?.city || '',
      senderCountry: this.shipment.customerInfo?.sender?.address?.country || '',

      // Receiver
      receiverName: this.shipment.customerInfo?.receiver?.name || '',
      receiverEmail: this.shipment.customerInfo?.receiver?.email || '',
      receiverPhone: this.formatter.formatPhone(this.shipment.customerInfo?.receiver?.phone) || '',
      receiverStreet: this.shipment.customerInfo?.receiver?.address?.street || '',
      receiverPostalCode: this.shipment.customerInfo?.receiver?.address?.zipCode || '',
      receiverCity: this.shipment.customerInfo?.receiver?.address?.city || '',
      receiverCountry: this.shipment.customerInfo?.receiver?.address?.country || '',

      // Parcel
      weight: this.shipment.parcelInfo?.weight || 0,
      length: this.shipment.parcelInfo?.dimensions?.length || 0,
      width: this.shipment.parcelInfo?.dimensions?.width || 0,
      height: this.shipment.parcelInfo?.dimensions?.height || 0,
      description: this.shipment.parcelInfo?.description || '',

      // #1573 — le prix et le statut ne sont plus chargés dans le formulaire.
      // Les replis `|| 0` / `|| ''` d'ici étaient la moitié amont du défaut :
      // sur un envoi sans `standardData.pricing`, ils lisaient `undefined` et
      // `saveChanges()` réécrivait `0`. Ces valeurs restent AFFICHÉES par le
      // template, en lecture seule, depuis `shipment` directement.
    });
  }

  /**
   * 🆕 Activer le mode édition (utilise notification service)
   */
  toggleEditMode(): void {
    if (!this.isAdmin) {
      this.notification.error('', 'ORDER_DETAILS.ERROR.ADMIN_ONLY');
      return;
    }

    this.isEditMode = !this.isEditMode;

    if (this.isEditMode) {
      this.populateForm();
    }
  }

  /**
   * #1640 — relire un téléphone édité à la main, sans jamais aplatir l'objet.
   *
   * ## Pourquoi ce n'est pas « ajouter deux clés au payload »
   *
   * `senderPhone` / `receiverPhone` étaient des contrôles LIÉS et ÉDITABLES
   * absents du payload : l'agent corrigeait un numéro, voyait « enregistré »,
   * et rien n'était écrit. C'est le canal des notifications SMS et WhatsApp.
   *
   * Mais le champ du formulaire est une CHAÎNE d'affichage
   * (`formatter.formatPhone()` → « +32 470000000 ») alors que Firestore stocke
   * un OBJET `{ prefix, number, whatsapp }`. Écrire naïvement
   * `'customerInfo.sender.phone': formValue.senderPhone` remplacerait l'objet
   * par une chaîne et casserait TOUS les consommateurs serveur, qui lisent
   * `.prefix` / `.number` : `notification-recipients`, `dpd-validator`,
   * `stripe/webhook`, `create-return-fee-payment-link`. Le remède naïf serait
   * pire que le défaut. On écrit donc les DEUX sous-champs, en chemins pointés
   * — `whatsapp` reste intact, `updateDoc` fusionne au niveau du champ.
   *
   * ## Trois issues, aucune silencieuse
   *
   * - saisie identique à ce qui a été chargé → **aucune clé** : c'est ce qui
   *   protège l'envoi sans téléphone, dont `formatPhone()` charge « N/A » dans
   *   le champ. Sans cette porte, un agent qui corrige une adresse écrirait
   *   « N/A » en numéro (même aller-retour que #1401 et #1573, ce fichier est
   *   déjà revenu deux fois sur cette classe).
   * - saisie modifiée et exploitable → `prefix` + `number` + `formatted`.
   * - saisie modifiée mais inexploitable → `'invalide'`, et `saveChanges()`
   *   REFUSE tout l'enregistrement avec un message. L'entre-deux — accepter
   *   sans écrire — est précisément ce que ce ticket corrige.
   *
   * L'indicatif absent de la saisie (« 0470 00 00 00 ») est repris de celui
   * déjà stocké : l'agent édite un numéro, pas un pays.
   */
  private relirePhoneEdite(
    role: 'sender' | 'receiver',
    saisie: unknown,
  ): Record<string, string> | 'invalide' {
    const stocke = this.shipment?.customerInfo?.[role]?.phone;
    const brut = typeof saisie === 'string' ? saisie : '';

    // Comparaison sur les seuls caractères signifiants : un espace ajouté ou
    // retiré n'est pas une modification.
    const signifiant = (v: string) => v.replace(/[\s.\-()]/g, '');
    if (signifiant(brut) === signifiant(this.formatter.formatPhone(stocke) || '')) {
      return {};
    }

    const lu = parsePhoneString(brut);
    const prefix = lu.prefix || (stocke && typeof stocke === 'object' ? stocke.prefix || '' : '');
    if (!prefix || lu.number.length < 6) {
      return 'invalide';
    }

    return {
      [`customerInfo.${role}.phone.prefix`]: prefix,
      [`customerInfo.${role}.phone.number`]: lu.number,
      // `formatted` est un doublon dérivé du modèle (`UnifiedPhoneInfo`). Le
      // laisser périmé rendrait deux valeurs contradictoires lisibles dans le
      // même document ; il se recompose comme côté envoi assisté.
      [`customerInfo.${role}.phone.formatted`]: `${prefix}${lu.number}`,
    };
  }

  /**
   * 🆕 Sauvegarder les modifications (utilise repository service)
   */
  async saveChanges(): Promise<void> {
    if (!this.editForm.valid) {
      this.notification.error('', 'ORDER_DETAILS.ERROR.FORM_INVALID');
      return;
    }

    this.isSaving = true;

    try {
      const formValue = this.editForm.value;

      // #1640 — les deux téléphones : édités par un widget, donc écrits.
      // `relirePhoneEdite()` refuse une saisie inexploitable AVANT toute
      // écriture ; on sort ici plutôt que d'écrire la moitié du formulaire.
      const telExpediteur = this.relirePhoneEdite('sender', formValue.senderPhone);
      const telDestinataire = this.relirePhoneEdite('receiver', formValue.receiverPhone);
      if (telExpediteur === 'invalide' || telDestinataire === 'invalide') {
        this.notification.error('', 'ORDER_DETAILS.ERROR.PHONE_INVALID');
        return; // `finally` remet `isSaving` à false.
      }

      // Construire l'objet de mise à jour
      const updatedData: any = {
        'customerInfo.sender.name': formValue.senderName,
        'customerInfo.sender.email': formValue.senderEmail,
        ...telExpediteur,
        ...telDestinataire,
        'customerInfo.sender.address.street': formValue.senderStreet,
        'customerInfo.sender.address.houseNumber': formValue.senderHouseNumber,
        'customerInfo.sender.address.zipCode': formValue.senderPostalCode,
        'customerInfo.sender.address.city': formValue.senderCity,
        'customerInfo.sender.address.country': formValue.senderCountry,

        'customerInfo.receiver.name': formValue.receiverName,
        'customerInfo.receiver.email': formValue.receiverEmail,
        'customerInfo.receiver.address.street': formValue.receiverStreet,
        'customerInfo.receiver.address.zipCode': formValue.receiverPostalCode,
        'customerInfo.receiver.address.city': formValue.receiverCity,
        'customerInfo.receiver.address.country': formValue.receiverCountry,

        'parcelInfo.weight': formValue.weight,
        'parcelInfo.dimensions.length': formValue.length,
        'parcelInfo.dimensions.width': formValue.width,
        'parcelInfo.dimensions.height': formValue.height,
        'parcelInfo.description': formValue.description
        // #1401 — `phase.current` n'est plus écrit ici. Le contrôle
        // `currentPhase` n'avait AUCUN widget dans le template : il rechargeait
        // la valeur lue puis la réécrivait telle quelle — ou écrivait `''`
        // quand le colis n'en portait pas, posant une phase vide. La phase se
        // dérive du statut, côté serveur (domains/shipment/shipment-phase.ts).
        //
        // 🔴 #1573 — MÊME FORME, MÊME FICHIER, six champs de plus. Le template
        // câble 20 contrôles (`[formControl]="$any(editForm.get('…'))"`) :
        // expéditeur, destinataire, poids, dimensions, description. AUCUN pour
        // `basePrice`, `weightSurcharge`, `serviceFee`, `taxes`, `total` ni
        // `currentStatus` — ils n'existent que dans `initForm()` et
        // `populateForm()`. Ils étaient pourtant soumis inconditionnellement,
        // dans le MÊME `updateDoc` atomique que l'adresse et le poids.
        //
        // Or `populateForm()` les lit avec un repli `|| 0` (prix) / `|| ''`
        // (statut). Sur un envoi dont `standardData.pricing` est absent ou
        // partiel — legacy, colis lourd dont le prix vit dans
        // `heavyData.quoteDetails.finalPrice` — le round-trip lisait
        // `undefined` et ÉCRIVAIT `0` : un agent qui corrigeait une adresse
        // mettait le prix à zéro, en production, sans aucun signal. Même
        // mécanique pour `status.current`, qui ne pouvait que se recopier ou
        // devenir `''`.
        //
        // Un prix ne s'édite pas ici : s'il doit l'être un jour (geste
        // commercial), c'est par une callable qui trace qui, quand et pourquoi
        // (CLAUDE.md § « Un prix n'existe qu'à UN endroit »). Le statut se
        // change par `quickAction()`, qui passe par la Cloud Function.
      };

      // Utiliser le repository service avec audit trail
      await this.repository.update(
        this.shipment!.id,
        updatedData,
        'manager' // operator
      );

      this.notification.success('', 'ORDER_DETAILS.SUCCESS.UPDATED');

      // #1573 — plus de déclenchement de notification de statut ici : ce
      // formulaire n'écrit plus `status.current`, donc il ne peut plus faire
      // changer le statut. `oldStatus !== newStatus` ne pouvait de toute façon
      // être vrai que dans un seul cas : un colis SANS statut, où le
      // round-trip comparait `undefined` à `''`. Le changement de statut passe
      // par `quickAction()` → Cloud Function, qui appelle
      // `triggerNotificationsForStatus` avec un statut réellement écrit.

      // Recharger les données
      this.loadShipmentDetails();
      this.isEditMode = false;
    } catch (error) {
      console.error('❌ Error updating shipment:', error);
      this.notification.error('', 'ORDER_DETAILS.ERROR.UPDATE_FAILED');
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * 📧 Déclencher les notifications pour un changement de statut
   */
  private async triggerNotificationsForStatus(newStatus: string): Promise<void> {
    // Statuts qui déclenchent des notifications (standard + heavy parcel)
    const notificationStatuses = [
      'collected_by_dpd', 'heavy_collected', 'received_at_warehouse', 'befret_transit',
      'local_sorting', 'out_for_delivery', 'awaiting_pickup', 'delivered',
      // Incident statuses
      'lost', 'damaged', 'poorly_packed', 'dangerous_goods',
      'seized_customs_eu', 'seized_customs_congo'
    ];

    if (!notificationStatuses.includes(newStatus)) {
      return; // Pas de notification pour ce statut
    }

    try {
      console.log('📧 Triggering notifications for status:', newStatus);

      const callable = httpsCallable(this.functions, 'triggerStatusNotification');
      const result: any = await callable({
        shipmentId: this.shipment!.id,
        newStatus: newStatus
      }).then(r => r.data);

      if (result.success) {
        this.notification.success('', 'Notifications envoyées avec succès');
        console.log('✅ Notifications sent:', result);
      } else {
        this.notification.warning('', 'Notifications partiellement envoyées');
        console.warn('⚠️ Partial notification success:', result);
      }
    } catch (error) {
      console.error('❌ Error triggering notifications:', error);
      this.notification.error('', 'Erreur lors de l\'envoi des notifications');
    }
  }

  /**
   * 🚀 ACTION RAPIDE: Changer statut + déclencher notifications en 1 clic
   * Méthode pragmatique pour faciliter la vie aux managers (isEmployee: true, ability: manager)
   *
   * #1586 — le second paramètre s'appelait `newPhase`, et rien ne l'écrivait :
   * la phase est posée par la CF, pas ici (voir le bloc de succès plus bas).
   * Il ne sert qu'à savoir s'il faut attacher un LIEU à l'événement, d'où son
   * nom actuel. Le renommer était le geste demandé par #1586 : c'est le mot
   * « phase » sur une valeur qui n'en était pas une qui a trompé une relecture.
   */
  async quickAction(newStatus: string, locationHint?: 'warehouse'): Promise<void> {
    if (!this.isAdmin) {
      this.notification.error('', 'ORDER_DETAILS.ERROR.ADMIN_ONLY');
      return;
    }

    if (!this.shipment) {
      return;
    }

    // Confirmation avant action
    const statusLabel = this.formatter.getStatusLabel(newStatus, this.shipment.category, this.shipment.webhookStatus);
    const confirmMsg = `Voulez-vous vraiment changer le statut vers "${statusLabel}" ?\n\nCela enverra automatiquement les notifications Email et SMS au destinataire.`;

    const confirmed = await this.notification.confirm(confirmMsg);
    if (!confirmed) {
      return;
    }

    this.isSaving = true;

    try {
      const oldStatus = this.shipment.status?.current;
      console.log('🚀 Quick Action via Cloud Function:', { oldStatus, newStatus, locationHint });

      // Utiliser la Cloud Function pour mise à jour cohérente
      // (statusHistory + timeline + status.current — tout en une fois)
      const callable = httpsCallable(this.functions, 'updateShipmentStatusManual');
      const result: any = await callable({
        trackingNumber: this.shipment.trackingNumber,
        newStatus: newStatus,
        notes: `Quick action: ${oldStatus} → ${newStatus}`,
        location: locationHint === 'warehouse' ? 'Tubize, BE' : undefined
      }).then(r => r.data);

      if (result?.success) {
        // ⚠️ Ne PAS réécrire la phase ici : la CF la pose déjà, dans la même
        // transaction que le statut (timeline-sync.service.ts pose 'phase.current',
        // 'phase.timeline', 'phase.progress' et 'currentPhase').
        //
        // L'écriture client qui vivait ici était redondante ET nuisible :
        //  1. elle écrasait la valeur serveur par le libellé porté par le bouton,
        //     dans un autre vocabulaire — le colis BF-BE02-948586-2 s'est retrouvé
        //     en production avec currentPhase='congo_distribution' d'un côté et
        //     phase.current='befret_distribution' de l'autre (30/07/2026) ;
        //  2. elle s'exécutait APRÈS un changement de statut déjà abouti, et son
        //     échec faisait tomber le catch — l'écran annonçait alors « Erreur lors
        //     du changement de statut » sur une opération réussie. Le gestionnaire
        //     recliquait, réappliquant le statut et risquant un second envoi de
        //     notifications au client.
        this.notification.success('', 'Statut mis à jour avec succès');

        // 📧 Déclencher les notifications automatiquement
        await this.triggerNotificationsForStatus(newStatus);

        // Recharger les données
        this.loadShipmentDetails();
      } else {
        this.notification.error('', 'Erreur lors du changement de statut');
        console.error('❌ Quick Action failed:', result);
      }
    } catch (error) {
      console.error('❌ Error in quick action:', error);
      // #1394 — la CF applique désormais les gardes de paiement (commande VM
      // impayée, supplément de poids, frais de retour, paiement contesté). Son
      // message dit LAQUELLE a refusé et quoi faire ; l'avaler derrière un
      // « Erreur » générique rendrait le refus inexplicable à l'écran.
      this.notification.error('', this.serverRefusalMessage(error) || 'Erreur lors du changement de statut');
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * 📋 Obtenir les actions rapides disponibles selon le statut actuel
   * Logique pragmatique basée sur le flow logistique
   * Supporte les colis standard ET heavy parcels
   */
  getAvailableQuickActions(): ManagerQuickAction[] {
    if (!this.shipment) {
      return [];
    }

    const currentStatus = this.shipment.status?.current as string;
    const isHeavyParcel = this.shipment.category === 'heavy';
    const actions: ManagerQuickAction[] = [];

    // ===== HEAVY PARCEL SPECIFIC ACTIONS =====
    // Collecte planifiée → Confirmer collecte effectuée
    if (isHeavyParcel && currentStatus === 'heavy_collection_scheduled') {
      actions.push({
        label: `📦 ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.CONFIRM_COLLECTION') || 'Confirmer collecte'}`,
        status: 'heavy_collected',
        icon: 'local_shipping',
        color: 'primary'
      });
    }

    // Collecté → Confirmer arrivée à l'entrepôt Tubize
    if (isHeavyParcel && currentStatus === 'heavy_collected') {
      actions.push({
        label: `🏭 ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.ARRIVED_WAREHOUSE') || 'Arrivé entrepôt Tubize'}`,
        status: 'received_at_warehouse',
        locationHint: 'warehouse',
        icon: 'warehouse',
        color: 'primary'
      });
    }

    // ===== EARLY PHASE: Post-payment, pre-warehouse (collect_request / home_pickup) =====
    // Ces actions permettent aux agents de progresser manuellement les colis
    // quand le DPD status sync ne fonctionne pas (bug getParcelLabelNumberForWebNumber)
    const earlyStatuses = [
      'collection_scheduled', 'dpd_shipment_created', 'ready_for_deposit',
      'label_generated', 'shipment_created', 'payment_completed'
    ];
    if (earlyStatuses.includes(currentStatus)) {
      actions.push({
        label: `📦 ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.CONFIRM_DPD_COLLECTION') || 'Confirmer collecte DPD'}`,
        status: 'collected_by_dpd',
        icon: 'local_shipping',
        color: 'primary'
      });
    }

    // Après collecte DPD → Arrivé à l'entrepôt
    if (currentStatus === 'collected_by_dpd' || currentStatus === 'dpd_transit_eu') {
      actions.push({
        label: `🏭 ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.ARRIVED_WAREHOUSE') || 'Arrivé entrepôt Tubize'}`,
        status: 'received_at_warehouse',
        locationHint: 'warehouse',
        icon: 'warehouse',
        color: 'primary'
      });
    }

    // ===== STANDARD + HEAVY POST-WAREHOUSE ACTIONS =====
    // Si le colis est à l'entrepôt → Départ vers Congo
    if (currentStatus === 'warehouse' || currentStatus === 'received_at_warehouse' || currentStatus === 'heavy_at_warehouse') {
      actions.push({
        label: `✈️ ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.DEPART_CONGO')}`,
        status: 'befret_transit',
        icon: 'flight_takeoff',
        color: 'primary'
      });
    }

    // Si en transit Befret → Arrivé au Congo (tri local)
    if (currentStatus === 'befret_transit') {
      actions.push({
        label: `🏭 ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.ARRIVED_CONGO')}`,
        status: 'local_sorting',
        icon: 'factory',
        color: 'primary'
      });
    }

    // Si arrivé au Congo ou tri local → En livraison OU Prêt au retrait
    if (currentStatus === 'awaiting_pickup' || currentStatus === 'local_sorting') {
      actions.push({
        label: `🚚 ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.OUT_FOR_DELIVERY')}`,
        status: 'out_for_delivery',
        icon: 'local_shipping',
        color: 'accent'
      });
      actions.push({
        label: `📍 ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.AWAITING_PICKUP')}`,
        status: 'awaiting_pickup',
        icon: 'store',
        color: 'accent'
      });
    }

    // Si en livraison ou en attente de retrait → Livré
    if (currentStatus === 'out_for_delivery' || currentStatus === 'awaiting_pickup') {
      actions.push({
        label: `✅ ${this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.MARK_DELIVERED')}`,
        status: 'delivered',
        icon: 'check_circle',
        color: 'success'
      });
    }

    return actions;
  }

  /**
   * 🚨 Actions d'incident — disponibles selon la phase actuelle du colis
   * Séparées des quick actions normales pour éviter les clics accidentels
   */
  getIncidentActions(): ManagerQuickAction[] {
    if (!this.shipment) return [];

    const currentStatus = this.shipment.status?.current as string;
    const actions: ManagerQuickAction[] = [];

    // Statuts terminaux → pas d'action incident possible
    const terminalStatuses = ['delivered', 'lost', 'damaged', 'poorly_packed', 'dangerous_goods',
      'seized_customs_eu', 'seized_customs_congo', 'returned_to_sender', 'delivery_failed',
      'heavy_delivered', 'heavy_delivered_with_installation'];
    if (terminalStatuses.includes(currentStatus)) return [];

    // Statuts post-collecte (colis en mouvement) → "Perdu" et "Endommagé" disponibles
    const postCollectionStatuses = [
      'collected_by_dpd', 'dpd_transit_eu', 'dpd_customs_check', 'arriving_befret',
      'received_at_warehouse', 'warehouse_processing', 'warehouse_sorted', 'warehouse_palletized',
      'ready_for_befret', 'befret_transit', 'customs_clearance', 'customs_held', 'customs_cleared',
      'local_sorting', 'out_for_delivery', 'delivery_attempted', 'awaiting_pickup',
      'heavy_collected', 'heavy_in_transit', 'heavy_at_warehouse', 'heavy_out_for_delivery'
    ];

    if (postCollectionStatuses.includes(currentStatus)) {
      actions.push({
        label: this.translate.instant('ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.LOST') || 'Colis perdu',
        status: 'lost',
        icon: 'search_off',
        color: 'danger'
      });
      actions.push({
        label: this.translate.instant('ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.DAMAGED') || 'Colis endommagé',
        status: 'damaged',
        icon: 'broken_image',
        color: 'danger'
      });
    }

    // À l'entrepôt → "Mal emballé" et "Contenu dangereux"
    const warehouseStatuses = ['received_at_warehouse', 'warehouse_processing', 'warehouse_sorted', 'warehouse_palletized', 'heavy_at_warehouse'];
    if (warehouseStatuses.includes(currentStatus)) {
      actions.push({
        label: this.translate.instant('ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.POORLY_PACKED') || 'Mal emballé',
        status: 'poorly_packed',
        icon: 'unarchive',
        color: 'warning'
      });
      actions.push({
        label: this.translate.instant('ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.DANGEROUS_GOODS') || 'Contenu dangereux',
        status: 'dangerous_goods',
        icon: 'dangerous',
        color: 'danger'
      });
    }

    // Transit EU / Douane EU / Entrepôt → "Saisi douane EU"
    const euCustomsStatuses = ['dpd_transit_eu', 'dpd_customs_check', 'arriving_befret', 'received_at_warehouse'];
    if (euCustomsStatuses.includes(currentStatus)) {
      actions.push({
        label: this.translate.instant('ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.SEIZED_CUSTOMS_EU') || 'Saisi douane EU',
        status: 'seized_customs_eu',
        icon: 'gavel',
        color: 'danger'
      });
    }

    // Transit Congo / Douane Congo / Arrivé Congo → "Saisi douane Congo"
    const congoCustomsStatuses = ['befret_transit', 'customs_clearance', 'customs_held', 'customs_cleared', 'awaiting_pickup', 'local_sorting'];
    if (congoCustomsStatuses.includes(currentStatus)) {
      actions.push({
        label: this.translate.instant('ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.SEIZED_CUSTOMS_CONGO') || 'Saisi douane Congo',
        status: 'seized_customs_congo',
        icon: 'gavel',
        color: 'danger'
      });
    }

    // Douane (EU ou Congo) → "Contenu dangereux" aussi
    const customsStatuses = ['customs_clearance', 'customs_held', 'dpd_customs_check'];
    if (customsStatuses.includes(currentStatus) && !warehouseStatuses.includes(currentStatus)) {
      actions.push({
        label: this.translate.instant('ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.DANGEROUS_GOODS') || 'Contenu dangereux',
        status: 'dangerous_goods',
        icon: 'dangerous',
        color: 'danger'
      });
    }

    return actions;
  }

  /**
   * 🆕 Vérifie si le colis est dans un état pré-entrepôt (collecte manuelle possible)
   * Statuts où la collecte manuelle est pertinente:
   * - dpd_shipment_created: Expédition DPD créée, en attente de collecte
   * - dpd_collection_scheduled: Collecte DPD planifiée
   * - pending_collection: En attente de collecte
   */
  isPreWarehouseStatus(): boolean {
    if (!this.shipment) return false;

    const currentStatus = this.shipment.status?.current;
    const preWarehouseStatuses = [
      'dpd_shipment_created',
      'dpd_collection_scheduled',
      'pending_collection',
      'order_confirmed',
      'payment_confirmed'
    ];

    return preWarehouseStatuses.includes(currentStatus);
  }

  /**
   * 🆕 ACTION RAPIDE: Collecte manuelle + Arrivée entrepôt
   * Utilise la Cloud Function updateShipmentStatusManual pour:
   * 1. Marquer le colis comme collecté manuellement (bypass DPD)
   * 2. Mettre à jour le statut vers "received_at_warehouse"
   * 3. Synchroniser statusHistory et timeline correctement
   */
  async quickActionManualCollection(): Promise<void> {
    if (!this.isAdmin) {
      this.notification.error('', 'ORDER_DETAILS.ERROR.ADMIN_ONLY');
      return;
    }

    if (!this.shipment) {
      return;
    }

    // Confirmation avant action
    const confirmMsg = this.translate.instant('ORDER_DETAILS_MANAGER.QUICK_ACTIONS.CONFIRM_MANUAL_COLLECTION') ||
      'Voulez-vous confirmer la collecte manuelle et marquer ce colis comme "Arrivé à l\'entrepôt" ?\n\nCette action bypass le flux DPD normal. Utilisez uniquement si le colis a été collecté directement par l\'équipe Befret.';

    const confirmed = await this.notification.confirm(confirmMsg);
    if (!confirmed) {
      return;
    }

    this.isSaving = true;

    try {
      console.log('🚀 [MANUAL COLLECTION] Calling updateShipmentStatusManual for:', this.shipment.trackingNumber);

      // Appeler la Cloud Function pour mise à jour cohérente
      const callable = httpsCallable(this.functions, 'updateShipmentStatusManual');
      const result: any = await callable({
        trackingNumber: this.shipment.trackingNumber,
        newStatus: 'received_at_warehouse',
        notes: 'Collecte manuelle - Bypass DPD (admin action)',
        location: 'Tubize, BE'
      }).then(r => r.data);

      if (result.success) {
        this.notification.success('', 'Colis marqué comme "Arrivé à l\'entrepôt" avec succès');
        console.log('✅ [MANUAL COLLECTION] Success:', result);

        // Déclencher les notifications pour le nouveau statut
        await this.triggerNotificationsForStatus('received_at_warehouse');

        // Reset le checkbox
        this.isManualCollection = false;

        // Recharger les données
        this.loadShipmentDetails();
      } else {
        this.notification.error('', 'Erreur lors de la mise à jour du statut');
        console.error('❌ [MANUAL COLLECTION] Failed:', result);
      }
    } catch (error) {
      console.error('❌ [MANUAL COLLECTION] Error:', error);
      // #1394 — même raison : la collecte manuelle vise `received_at_warehouse`,
      // la cible gardée par #403 et #1349.
      this.notification.error('', this.serverRefusalMessage(error) || 'Erreur lors de la collecte manuelle');
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Message de refus rendu par une Cloud Function, quand il y en a un (#1394).
   *
   * `HttpsError` arrive côté client avec son `message` : c'est le texte métier
   * (« Commande VM en attente de paiement… »), pas une trace technique. On ne
   * l'affiche que pour les refus MÉTIER (`failed-precondition`,
   * `invalid-argument`, `not-found`) — un `internal` ou une panne réseau n'a
   * rien à dire à l'utilisateur, et son message ne doit pas fuiter à l'écran.
   */
  private serverRefusalMessage(error: unknown): string | null {
    const code = (error as { code?: string })?.code || '';
    const message = (error as { message?: string })?.message || '';
    const businessCodes = [
      'failed-precondition', 'invalid-argument', 'not-found',
      'functions/failed-precondition', 'functions/invalid-argument', 'functions/not-found',
    ];
    return businessCodes.includes(code) && message ? message : null;
  }

  /**
   * Annuler l'édition
   */
  cancelEdit(): void {
    this.isEditMode = false;
    this.editForm.reset();
  }

  /**
   * 🆕 Supprimer la commande (utilise repository service avec soft delete)
   */
  async deleteShipment(): Promise<void> {
    if (!this.isAdmin) {
      this.notification.error('', 'ORDER_DETAILS.ERROR.ADMIN_ONLY');
      return;
    }

    const confirmMsg = this.translate.instant('ORDER_DETAILS.CONFIRM.DELETE', {
      trackingID: this.trackingID
    });

    const confirmed = await this.notification.confirm(confirmMsg);
    if (!confirmed) {
      return;
    }

    try {
      // Soft delete avec audit trail
      await this.repository.delete(this.shipment!.id, 'manager');

      this.notification.success('', 'ORDER_DETAILS.SUCCESS.DELETED');

      // Retour à la liste
      this.router.navigate(['/parcel-list']);
    } catch (error) {
      console.error('❌ Error deleting shipment:', error);
      this.notification.error('', 'ORDER_DETAILS.ERROR.DELETE_FAILED');
    }
  }

  /**
   * Restaurer un colis supprimé (soft delete → active)
   */
  async restoreShipment(): Promise<void> {
    if (!this.isAdmin || !this.shipment) {
      return;
    }

    const confirmed = await this.notification.confirm('Restaurer ce colis ?');
    if (!confirmed) {
      return;
    }

    try {
      await this.repository.restore(this.shipment.id, 'manager');
      this.notification.success('', 'Colis restauré avec succès');
      // Force refresh to update the view
      this.repository.invalidateCache(this.trackingID);
      this.loadShipmentDetails();
    } catch (error) {
      console.error('❌ Error restoring shipment:', error);
      this.notification.error('', 'Erreur lors de la restauration');
    }
  }

  /**
   * 🆕 Charger les détails (utilise repository service avec cache)
   */
  private loadShipmentDetails(): void {
    this.isLoading = true;
    this.error = '';

    this.repository.getByTrackingNumber(this.trackingID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (shipment) => {
          if (shipment) {
            // ✅ Vérifier que le paiement est complété OU colis lourd (quote-based)
            const hasPayment = shipment.paymentFlow?.paymentStatus === 'paid' ||
                               shipment.paymentFlow?.webhookProcessed === true ||
                               shipment.standardData?.paymentIntentId ||
                               shipment.standardData?.dpdIntegration?.shipmentId;

            // ✅ Les colis lourds (category === 'heavy') sont affichés même sans paiement
            const isHeavyParcel = shipment.category === 'heavy';

            if (!hasPayment && !isHeavyParcel) {
              // Shipment non payé (et pas colis lourd) = masqué (même pour les admins)
              console.warn('⚠️ [MANAGER] Shipment non payé masqué:', this.trackingID);
              this.error = 'ORDER_DETAILS.ERROR.NOT_FOUND';
              this.isLoading = false;
              return;
            }

            this.shipment = shipment;
            console.log('✅ Shipment loaded:', this.shipment);
            this.loadNotificationLogs();
          } else {
            this.error = 'ORDER_DETAILS.ERROR.NOT_FOUND';
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('❌ Error loading shipment:', error);
          this.error = 'ORDER_DETAILS.ERROR.LOAD_FAILED';
          this.isLoading = false;
        }
      });
  }

  /**
   * Retour à la liste des envois
   */
  goBack(): void {
    this.router.navigate(['/parcel-list']);
  }

  /**
   * Redirection vers la page de traitement colis lourd
   */
  goToHeavyProcessing(): void {
    if (!this.shipment) {
      this.notification.error('', 'Aucun colis sélectionné');
      return;
    }

    if (this.shipment.category !== 'heavy') {
      this.notification.error('', 'Ce n\'est pas un colis lourd');
      return;
    }

    // #1902 lot 1 — l'écran legacy `/heavy-parcel-processing/:id` est retiré :
    // le traitement d'un colis lourd se fait dans le back-office.
    this.router.navigate(['/backoffice/heavy-processing', this.shipment.id]);
  }

  /**
   * Aller vers la page de tracking client
   */
  goToTracking(): void {
    this.router.navigate(['/details'], { queryParams: { trackingID: this.trackingID } });
  }

  /**
   * #1857 lot 7 — le bouton ne s'affiche que s'il existe un vrai PDF. `labelUrl` est le lien de
   * SUIVI DPD : une collecte à domicile en porte un sans avoir d'étiquette, et le téléchargement
   * échouait (« Veuillez réessayer »).
   */
  canDownloadLabel(): boolean {
    return hasDownloadableLabel(this.shipment);
  }

  /**
   * 🆕 Télécharger l'étiquette DPD (PDF uniquement)
   * Priorité:
   * 1. labelPublicUrl (Cloud Storage - signée URL vers PDF) ✅
   * 2. standardData.labelPdf (base64 PDF - anciens shipments) ✅
   * 3. webhookStatus.stages.label.labelPdf (base64 PDF - legacy location) ✅
   *
   * Note: labelUrl et dpdIntegration.labelUrl sont des URLs de TRACKING, pas des PDFs!
   */
  downloadLabel(): void {
    const trackingNumber = this.shipment?.trackingNumber || this.trackingID;

    if (!trackingNumber) {
      this.notification.error('', 'ORDER_DETAILS.ERROR.NO_TRACKING');
      return;
    }

    // Méthode 1: Cloud Storage (avec gestion des URLs expirées)
    const labelPublicUrl = this.shipment?.standardData?.labelPublicUrl;
    const isExpired = this.labelDownloadService.isUrlExpired(labelPublicUrl || '');

    if (isExpired || !labelPublicUrl) {
      console.log('🔄 [MANAGER] URL expirée ou manquante, régénération...');

      // Obtenir une URL fraîche via Cloud Function
      this.labelDownloadService.downloadLabel(trackingNumber, `etiquette-${trackingNumber}.pdf`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            console.log('✅ Étiquette téléchargée avec succès');
            this.notification.success('', 'ORDER_DETAILS.SUCCESS.LABEL_DOWNLOADED');
          },
          error: (error) => {
            console.error('❌ Erreur téléchargement, fallback vers base64:', error);
            // Fallback vers base64 si la Cloud Function échoue
            this.downloadLabelFallback();
          }
        });
      return;
    }

    // URL encore valide
    console.log('📥 Téléchargement étiquette depuis Cloud Storage:', labelPublicUrl);
    window.open(labelPublicUrl, '_blank');
  }

  /**
   * Fallback: télécharger depuis base64 (anciens shipments)
   */
  private downloadLabelFallback(): void {
    // Méthode 2: Base64 PDF dans standardData (anciens shipments)
    const labelPdf = this.shipment?.standardData?.labelPdf;
    if (labelPdf && typeof labelPdf === 'string' && labelPdf.length > 100) {
      console.log('📥 Téléchargement étiquette depuis base64 PDF (standardData)');
      this.downloadLabelFromBase64(labelPdf);
      return;
    }

    // Méthode 3: Base64 PDF dans webhookStatus (legacy location for old shipments)
    const webhookLabelPdf = this.shipment?.webhookStatus?.stages?.label?.labelPdf;
    if (webhookLabelPdf && typeof webhookLabelPdf === 'string' && webhookLabelPdf.length > 100) {
      console.log('📥 Téléchargement étiquette depuis webhookStatus base64 PDF (legacy)');
      this.downloadLabelFromBase64(webhookLabelPdf);
      return;
    }

    // Aucune étiquette PDF disponible
    console.warn('⚠️ Aucune étiquette PDF disponible pour ce shipment');
    this.notification.error('', 'ORDER_DETAILS.ERROR.NO_LABEL');
  }

  /**
   * Télécharger l'étiquette depuis base64 (fallback pour anciens shipments)
   */
  private downloadLabelFromBase64(labelPdf: string): void {
    try {
      const byteCharacters = atob(labelPdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `befret-label-${this.trackingID}.pdf`;
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Erreur téléchargement base64 PDF:', error);
      this.notification.error('', 'ORDER_DETAILS.ERROR.LABEL_DOWNLOAD_FAILED');
    }
  }

  /**
   * Exporter les données en JSON
   */
  exportJSON(): void {
    if (!this.shipment) return;

    const dataStr = JSON.stringify(this.shipment, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shipment-${this.trackingID}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * 🆕 Copier dans le clipboard (utilise notification service)
   */
  copyToClipboard(text: string, label: string): void {
    this.notification.copyToClipboard(text, label);
  }

  /**
   * Toggle section collapse/expand
   */
  toggleSection(section: string): void {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  /**
   * Imprimer la page
   */
  printPage(): void {
    window.print();
  }

  // ===== NOTIFICATION LOGS =====

  private loadNotificationLogs(): void {
    if (!this.trackingID) return;

    this.repository.getNotificationLogs(this.trackingID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (logs) => {
          this.notificationLogs = logs;
        },
        error: (error: any) => {
          console.error('❌ Error loading notification logs:', error);
          this.notificationLogs = [];
        }
      });
  }

  async retryNotification(logId: string): Promise<void> {
    if (this.isRetrying[logId]) return;

    this.isRetrying[logId] = true;

    try {
      const callable = httpsCallable(this.functions, 'retryNotification');
      const result: any = await callable({ notificationResultId: logId }).then(r => r.data);

      if (result?.success) {
        this.notification.success('', this.translate.instant('ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.RETRY_SUCCESS'));
      } else {
        this.notification.error('', this.translate.instant('ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.RETRY_ERROR'));
      }
    } catch (error) {
      console.error('❌ Error retrying notification:', error);
      this.notification.error('', this.translate.instant('ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.RETRY_ERROR'));
    } finally {
      this.isRetrying[logId] = false;
    }
  }

  getNotificationTypeLabel(notificationType: string): string {
    if (!notificationType) return '';
    const baseType = notificationType.startsWith('incident_') ? 'incident' : notificationType;
    return this.translate.instant(`ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.NOTIF_${baseType}`);
  }

  formatNotificationDate(timestamp: any): string {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
      + ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }

  /**
   * #1571 — la clé du libellé de remise RDC, JAMAIS la valeur persistée.
   *
   * Le template concaténait directement `parcelInfo.deliveryMethod` au préfixe
   * du catalogue. Tant que la valeur appartient au catalogue le résultat est
   * correct par accident ; dès qu'une valeur en tombe — et le vocabulaire de
   * l'axe RDC a justement été resserré par #1548 — l'écran affiche la CLÉ BRUTE
   * (`ORDER_DETAILS_MANAGER.VALUES.DELIVERY_METHODS.pickup_point`) à un agent,
   * sans que rien ne le signale.
   *
   * Le normaliseur ramène les formes legacy connues aux deux valeurs
   * canoniques ; une valeur qu'il ne reconnaît pas rend une clé `UNKNOWN`
   * explicite. Dans les deux cas la clé rendue existe dans les trois catalogues,
   * donc aucune valeur persistée ne peut plus fuir à l'écran.
   */
  /**
   * Valeur déclarée par le client (somme des articles), affichée au staff pour
   * les réclamations. `null` ⇒ le template affiche « non déclarée » : aucun
   * montant n'est inventé. Lecture seule — aucun effet sur le prix ni sur DPD.
   */
  get declaredValue(): DeclaredValue | null {
    return readDeclaredValue(this.shipment);
  }

  deliveryMethodLabelKey(deliveryMethod: unknown): string {
    const normalized = normalizeBefretDeliveryMethod(deliveryMethod);
    return `ORDER_DETAILS_MANAGER.VALUES.DELIVERY_METHODS.${normalized ?? 'UNKNOWN'}`;
  }
}
